import type { TFunction } from 'i18next';

/** i18next t bound to `agency`, or the looser (key, opts) function some popovers receive as a prop. */
export type LabelT = TFunction<'agency'> | ((key: string, opts?: Record<string, unknown>) => string);

export type PortfolioHealthTier = 'on_track' | 'watch' | 'at_risk' | 'off_track' | 'paused';

/** Localized health-tier label (static keys so the i18n audit can see them). */
export const tierLabel = (t: LabelT, tier: PortfolioHealthTier): string => {
  switch (tier) {
    case 'on_track': return t('portfolio_tier_on_track', { defaultValue: 'On track' });
    case 'watch': return t('portfolio_tier_watch', { defaultValue: 'Watch' });
    case 'at_risk': return t('portfolio_tier_at_risk', { defaultValue: 'At risk' });
    case 'off_track': return t('portfolio_tier_off_track', { defaultValue: 'Off track' });
    default: return t('portfolio_tier_paused', { defaultValue: 'Paused' });
  }
};

/** Localized lifecycle-stage label. */
export const lifecycleStageLabel = (t: LabelT, stage: string): string => {
  switch (stage) {
    case 'onboarding': return t('portfolio_lifecycle_onboarding', { defaultValue: 'Onboarding' });
    case 'churning': return t('portfolio_lifecycle_churning', { defaultValue: 'Deprioritizing' });
    case 'paused': return t('portfolio_lifecycle_paused', { defaultValue: 'Paused' });
    default: return t('portfolio_lifecycle_active', { defaultValue: 'Active' });
  }
};

// Health signals carry a stable `key` from the API; `label` is the English fallback.
// Static keys (no template strings) so i18n-verify-dynamic-keys can see them.
export function healthSignalLabel(
  t: LabelT,
  sig: { key?: string; label: string },
): string {
  switch (sig.key) {
    case 'approval_velocity': return t('portfolio_health_signal_approval_velocity', { defaultValue: 'Approval velocity' });
    case 'scheduling_runway': return t('portfolio_health_signal_scheduling_runway', { defaultValue: 'Scheduling runway' });
    case 'channel_health': return t('portfolio_health_signal_channel_health', { defaultValue: 'Channel health' });
    case 'no_content': return t('portfolio_health_signal_no_content', { defaultValue: 'No content yet' });
    case 'cadence': return t('portfolio_health_signal_cadence', { defaultValue: 'Cadence' });
    case 'library_freshness': return t('portfolio_health_signal_library_freshness', { defaultValue: 'Library freshness' });
    case 'satisfaction': return t('portfolio_health_signal_satisfaction', { defaultValue: 'Satisfaction' });
    case 'renewal_window': return t('portfolio_health_signal_renewal_window', { defaultValue: 'Renewal window' });
    case 'paused': return t('portfolio_health_signal_paused', { defaultValue: 'Paused' });
    default: return sig.label;
  }
}

/** Localized status-update colour-pill label (ON_TRACK, WATCH, ...). Static keys for the i18n audit. */
export const statusPillLabel = (t: LabelT, pill: string): string => {
  switch (pill) {
    case 'ON_TRACK': return t('portfolio_status_pill_on_track', { defaultValue: 'On track' });
    case 'WATCH': return t('portfolio_status_pill_watch', { defaultValue: 'Watch' });
    case 'AT_RISK': return t('portfolio_status_pill_at_risk', { defaultValue: 'At risk' });
    case 'OFF_TRACK': return t('portfolio_status_pill_off_track', { defaultValue: 'Off track' });
    case 'ON_HOLD': return t('portfolio_status_pill_on_hold', { defaultValue: 'On hold' });
    case 'PAUSED': return t('portfolio_status_pill_paused', { defaultValue: 'Paused' });
    default: return t('portfolio_status_pill_complete', { defaultValue: 'Complete' });
  }
};

/** Localized status-update vibe label (STRONG, NEUTRAL, TENSE, ON_FIRE). */
export const statusVibeLabel = (t: LabelT, vibe: string): string => {
  switch (vibe) {
    case 'STRONG': return t('portfolio_status_vibe_strong', { defaultValue: 'Strong' });
    case 'TENSE': return t('portfolio_status_vibe_tense', { defaultValue: 'Tense' });
    case 'ON_FIRE': return t('portfolio_status_vibe_on_fire', { defaultValue: 'On fire' });
    default: return t('portfolio_status_vibe_neutral', { defaultValue: 'Neutral' });
  }
};
